import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { gql } from 'graphql-tag';
import get from 'lodash.get';
import { transformTasksForDropdown } from '../util';

const GET_TASKS_FOR_PROJECT = gql`
  query getTasksForProject(
    $projectId: String!
    $isOpen: Boolean
    $textSearch: String
    $page: Int!
    $pageSize: Int!
    $isTimeEntryAllowed: Boolean
  ) {
    project(projectId: $projectId) {
      id
      isTimeEntryAllowed
      tasks(
        isOpen: $isOpen
        textSearch: $textSearch
        page: $page
        pageSize: $pageSize
        isTimeEntryAllowed: $isTimeEntryAllowed
      ) {
        id
        uri
        name
        displayText
        code
        isClosed
        parent {
          id
          uri
          name
          displayText
        }
        fullPath {
          id
          uri
          name
          displayText
        }
      }
    }
  }
`;

export const useTaskDropdownOptions = ({
  projectId,
  searchTerm = '',
  showClosedTasks = false,
  skip = false
}) => {
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, loading } = useQuery(GET_TASKS_FOR_PROJECT, {
    variables: {
      projectId,
      isOpen: showClosedTasks ? undefined : true,
      textSearch: debouncedSearchTerm || undefined,
      page: 1,
      pageSize: 100,
      isTimeEntryAllowed: true
    },
    skip,
    fetchPolicy: 'network-only'
  });

  const tasks = useMemo(() => {
    const taskList = get(data, 'project.tasks', []);

    return transformTasksForDropdown(taskList);
  }, [data]);

  const isTimeEntryAllowedForProject = useMemo(() => {
    return get(data, 'project.isTimeEntryAllowed', true);
  }, [data]);

  return {
    loading: loading && !!projectId,
    tasks,
    hasMore: false,
    isTimeEntryAllowedForProject
  };
};

export default useTaskDropdownOptions;
